import omit from 'lodash/omit';
import apiUtils from '@utils/apiUtils';
import { apiConstants } from '../utils/constants';
import { fetchAPI } from './apiServiceHelper';
import { normalizeQueryParams as apiUtilsNormalizeQueryParams } from './apiUtils';

const defaultGetParam = apiUtils.getDefaultGetParams();
const defaultGetParamCustomer = apiUtils.getDefaultGetParamsCustomer();

let api = {
    user: {
        partners: {
            get: ({ offset = 0, limit = 1000, filter = {} }) => {
                return fetchAPI.getListRaw(`${apiConstants.BASE_URL}/api/partner`, {
                    offset,
                    limit,
                    partnerIds: filter.partnerIds.join(),
                });
            },
        },
    },
    customers: {
        get: ({
            offset = defaultGetParam.offset,
            limit = defaultGetParam.limit,
            filter = {
                email: defaultGetParamCustomer.email,
                lastName: defaultGetParamCustomer.lastName,
                loyaltyNumber: defaultGetParamCustomer.loyaltyNumber,
                partnerIds: defaultGetParamCustomer.partnerIds,
                searchCriterion: defaultGetParam.searchCriterion,
            },
            sort = '',
        }) => {
            filter.partnerIds = filter.partnerIds.join();
            return fetchAPI.getList(
                `${apiConstants.BASE_URL}/api/customer`,
                apiUtils.normalizeQueryParams({
                    ...filter,
                    limit,
                    offset,
                    sort,
                })
            );
        },
        update: (customerId, partnerId, payload) => {
            return fetchAPI.put(
                `${apiConstants.BASE_URL}/api/customer/${customerId}/partner/${partnerId}`,
                { ...payload }
            );
        },
    },
    loyaltyTransactions: {
        get: ({ offset = defaultGetParam.offset, limit = defaultGetParam.limit, filter }) => {
            return fetchAPI.getList(
                `${apiConstants.BASE_URL}/api/customer/transaction`,
                apiUtils.normalizeQueryParams({
                    ...filter,
                    limit,
                    offset,
                })
            );
        },
    },
    orders: {
        resendConfirmationEmail: (props) => {
            return fetchAPI.post(
                `${apiConstants.BASE_URL}/api/order/${props.orderId}/hotel/confirmation-email`,
                props.hotelRoomInfo
            );
        },
        resendCancellationEmail: (props) => {
            return fetchAPI.post(
                `${apiConstants.BASE_URL}/api/order/${props.orderId}/hotel/cancellation-email`,
                props.hotelRoomInfo
            );
        },
        resendActionRequiredEmail: (props) => {
            return fetchAPI.post(
                `${apiConstants.BASE_URL}/api/order/${props.orderUuid}/email/action-required`
            );
        },
        cancelBooking: (props) => {
            return fetchAPI.delete(
                `${apiConstants.BASE_URL}/api/order/${props.orderId}/order-partner/${props.orderPartnerId}`
            );
        },
        refundBooking: (props) => {
            return fetchAPI.post(
                `${apiConstants.BASE_URL}/api/order/${props.orderUuid}/booking/refunds`
            );
        },
        updateChannel: (props) => {
            return fetchAPI.put(`${apiConstants.BASE_URL}/api/order/${props.orderId}/token`, {
                tokenUuid: props.newChannel,
            });
        },
        getOrdersByCustomerId({
            offset = defaultGetParam.offset,
            limit = defaultGetParam.limit,
            filter,
        }) {
            return fetchAPI.getList(
                `${apiConstants.BASE_URL}/api/customer/${filter.id}/order`,
                apiUtils.normalizeQueryParams({
                    ...omit(filter, 'id'),
                    limit,
                    offset,
                })
            );
        },
        one: (id) => {
            return fetchAPI.get(`${apiConstants.BASE_URL}/api/order/${id}`);
        },
        getHotelSupplierBookingStatus(orderId, orderPartnerId) {
            return fetchAPI.put(
                `${apiConstants.BASE_URL}/api/order/${orderId}/order-partner/${orderPartnerId}/status`
            );
        },
        getTrueCancellationPolicies: (orderId) => {
            return fetchAPI.get(
                `${apiConstants.BASE_URL}/api/order/${orderId}/cancellation-policies`
            );
        },
        statistics: {
            get: ({ offset = defaultGetParam.offset, limit = defaultGetParam.limit, filter }) => {
                return fetchAPI.get(
                    `${apiConstants.BASE_URL}/api/order/statistics`,
                    apiUtils.normalizeQueryParams({
                        ...filter,
                        limit,
                        offset,
                    })
                );
            },
        },
    },
    reports: {
        customersActivities: {
            get: ({ offset = defaultGetParam.offset, limit = defaultGetParam.limit, filter }) => {
                filter.partnerIds =
                    filter.partnerIds && filter.partnerIds.length > 0
                        ? filter.partnerIds.join()
                        : [];
                return fetchAPI.getList(
                    `${apiConstants.BASE_URL}/api/customer/report`,
                    apiUtils.normalizeQueryParams({
                        ...filter,
                        limit,
                        offset,
                    })
                );
            },
        },
        financeCommission: {
            get: ({ offset = defaultGetParam.offset, limit = defaultGetParam.limit, filter }) => {
                filter.partnerIds =
                    filter.partnerIds && filter.partnerIds.length > 0
                        ? filter.partnerIds.join()
                        : [];
                return fetchAPI.getList(
                    `${apiConstants.BASE_URL}/api/order/finance`,
                    apiUtils.normalizeQueryParams({
                        ...filter,
                        limit,
                        offset,
                    })
                );
            },
        },
        eventOrderDetails: {
            get: ({ offset = defaultGetParam.offset, limit = defaultGetParam.limit, filter }) => {
                filter.partnerIds =
                    filter.partnerIds && filter.partnerIds.length > 0
                        ? filter.partnerIds.join()
                        : [];
                return fetchAPI.getList(
                    `${apiConstants.BASE_URL}/api/order/event/report/v2`,
                    apiUtils.normalizeQueryParams({
                        ...filter,
                        limit,
                        offset,
                    })
                );
            },
        },
        orders: {
            get: ({ offset = defaultGetParam.offset, limit = defaultGetParam.limit, filter }) => {
                filter.partnerIds =
                    filter.partnerIds && filter.partnerIds.length > 0
                        ? filter.partnerIds.join()
                        : [];
                filter.currency =
                    filter.currency && filter.currency.length > 0 ? filter.currency.join() : [];
                filter.productTypesIds =
                    filter.productTypesIds && filter.productTypesIds.length > 0
                        ? filter.productTypesIds.join()
                        : [];
                return fetchAPI.getList(
                    `${apiConstants.BASE_URL}/api/order/report`,
                    apiUtils.normalizeQueryParams({
                        ...filter,
                        limit,
                        offset,
                    })
                );
            },
        },
    },
    product: {
        categories: {
            get: () => {
                return fetchAPI.getList(`${apiConstants.BASE_URL}/api/category`, {
                    reporting: true,
                });
            },
        },
        providers: {
            get: () => {
                return fetchAPI.getListRaw(`${apiConstants.BASE_URL}/api/hotels/providers`, {
                    reporting: true,
                });
            },
        },
    },
    topSellingEvents: {
        events: {
            get: (params) => {
                return fetchAPI.post(`${apiConstants.BASE_URL}/api/event/report`, params);
            },
        },
        sameDateEvents: {
            get: (params) => {
                return fetchAPI.post(`${apiConstants.BASE_URL}/api/event/report`, params);
            },
        },
        categories: {
            get: () => {
                return fetchAPI.getList(`${apiConstants.BASE_URL}/api/event/segment`);
            },
        },
        genre: {
            get: (params = {}) => {
                const { segmentIds = [] } = params;
                const urlParams = segmentIds.map((id) => `segmentIds=${id}`).join('&');

                return fetchAPI.getList(
                    `${apiConstants.BASE_URL}/api/event/genre?${urlParams}`,
                    params
                );
            },
        },
    },
    countries: {
        get: () => {
            return fetchAPI.getList(`${apiConstants.BASE_URL}/api/countries`);
        },
    },
    states: {
        get: ({ filter = {} }) => {
            return fetchAPI.getList(`${apiConstants.BASE_URL}/api/country/${filter.code}/states`);
        },
    },
    cities: {
        get: ({
            keyword = '',
            countryCode,
            stateCode,
            limit = 1000,
            offset = defaultGetParam.offset,
            onlyFavourite = false,
        } = defaultGetParam) => {
            return fetchAPI.getList(
                `${apiConstants.BASE_URL}/api/city`,
                apiUtilsNormalizeQueryParams({
                    limit,
                    offset,
                    keyword,
                    countryCode,
                    stateCode,
                    onlyFavourite,
                })
            );
        },
    },
    attractions: {
        get: ({ limit, offset, keyword }) => {
            return fetchAPI.getList(`${apiConstants.BASE_URL}/api/attraction`, {
                limit,
                offset,
                keyword,
            });
        },
        getOne: (id) => {
            return fetchAPI.get(`${apiConstants.BASE_URL}/api/attraction/${id}`);
        },
    },
    events: {
        // Active events that haven't ended, by name; without a keyword, the first ones by date.
        search: ({ keyword = '', limit = 20 } = {}) => {
            return fetchAPI.getList(
                `${apiConstants.BASE_URL}/api/product`,
                apiUtils.normalizeQueryParams({ keyword, limit })
            );
        },
        getOne: (id) => {
            return fetchAPI.get(`${apiConstants.BASE_URL}/api/product/${id}`);
        },
    },
    venues: {
        // Venues that have active events, minus the tenant's excluded ones, by name; without a
        // keyword, the first ones by name.
        search: ({ keyword = '', limit = 20 } = {}) => {
            return fetchAPI.getList(
                `${apiConstants.BASE_URL}/api/venue`,
                apiUtils.normalizeQueryParams({ keyword, limit })
            );
        },
        getOne: (id) => {
            return fetchAPI.get(`${apiConstants.BASE_URL}/api/venue/${id}`);
        },
    },
    hotels: {
        // Active hotels whose name has all the words of `name` (the last one partial), or whose
        // provider hotel id starts with `providerHotelId`, most relevant first: a plain array of
        // { hotelId, partnerHotelId, provider, name }, where the provider can be null. There is no
        // search by GST id. Send one of the two: with both, `providerHotelId` wins, and with neither
        // (a blank value counts as not sent) the API answers 400; `limit` goes up to 100.
        search: ({ name, providerHotelId, limit = 20 } = {}) => {
            return fetchAPI.getListRaw(
                `${apiConstants.BASE_URL}/api/hotel/provider-hotels`,
                apiUtils.normalizeQueryParams({ name, providerHotelId, limit })
            );
        },
    },
    currencies: {
        get: ({ filter: { withEntity } }) => {
            return fetchAPI.getList(`${apiConstants.BASE_URL}/api/currencies?with=${withEntity}`);
        },
    },
    bookingStatuses: {
        get: () => {
            return fetchAPI.getListRaw(`${apiConstants.BASE_URL}/api/hotels/booking/status`);
        },
    },
    offerTokens: {
        get: ({
            limit = defaultGetParam.limit,
            offset = defaultGetParam.offset,
            filter = {},
        } = {}) => {
            return fetchAPI.getList(
                `${apiConstants.BASE_URL}/api/offer-tokens`,
                apiUtils.normalizeQueryParams({
                    limit,
                    offset,
                    offerId: filter.offerToken,
                    name: filter.offerTokenName,
                    active: filter.offerTokenActive,
                })
            );
        },
        getOne: (offerToken) => {
            return fetchAPI.getList(
                `${apiConstants.BASE_URL}/api/offer-tokens`,
                apiUtils.normalizeQueryParams({ offerId: offerToken })
            );
        },
        // Active tokens whose value contains `offerId` and whose name contains `name`, in any case;
        // either can be left out, and with neither, the first tokens. The API pages by cursor and
        // sends 5 by default, so 20 are asked for.
        search: ({ offerId, name, limit = 20 } = {}) => {
            return fetchAPI.getList(
                `${apiConstants.BASE_URL}/api/offer-tokens`,
                apiUtils.normalizeQueryParams({ offerId, name, active: true, limit })
            );
        },
        create: (offer) => {
            const payload = {
                tokenValue: offer.offerToken,
                name: offer.offerTokenName,
                active: offer.offerTokenActive,
                ...(offer.priceRuleset && { priceRulesetId: offer.priceRuleset.value }),
            };

            return fetchAPI.post(`${apiConstants.BASE_URL}/api/offer-tokens`, payload);
        },
        update: (offerToken, data) => {
            const payload = {
                name: data.offerTokenName,
                active: data.offerTokenActive,
                ...(data.priceRuleset && { priceRulesetId: data.priceRuleset.value }),
            };

            return fetchAPI.put(`${apiConstants.BASE_URL}/api/offer-tokens/${offerToken}`, payload);
        },
    },
    priceRulesets: {
        get: ({
            limit = defaultGetParam.limit,
            offset = defaultGetParam.offset,
            keyword = '',
        } = {}) => {
            return fetchAPI.getList(
                `${apiConstants.BASE_URL}/api/price-ruleset`,
                apiUtils.normalizeQueryParams({
                    limit,
                    offset,
                    searchTerm: keyword,
                })
            );
        },
    },
};

export default api;
